import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Plus, Pencil, Trash2, X, Search, Link, MinusCircle, History, ClipboardList } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL  = { peralatan: 'Peralatan', perlengkapan: 'Perlengkapan', utilitas: 'Utilitas' };
const CATEGORY_BADGE  = { peralatan: 'bg-purple-100 text-purple-700', perlengkapan: 'bg-blue-100 text-blue-700', utilitas: 'bg-teal-100 text-teal-700' };
const CONDITION_LABEL = { baik: 'Baik', rusak_ringan: 'Rusak Ringan', rusak_berat: 'Rusak Berat' };
const CONDITION_BADGE = { baik: 'bg-green-100 text-green-700', rusak_ringan: 'bg-yellow-100 text-yellow-700', rusak_berat: 'bg-red-100 text-red-700' };

const TUJUAN_OPTIONS = ['Perbaikan', 'Kebersihan', 'Perawatan Rutin', 'Perlengkapan Kamar', 'Keperluan Kantor', 'Lainnya'];

const fmtDate = d => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const fmtPurchaseDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jakarta',
  });
};
const emptyForm = () => ({ property_name: '', category: 'peralatan', item_name: '', quantity: 1, unit: 'pcs', condition_status: 'baik', location: '', notes: '' });
const emptyUsage = () => ({ qty_used: 1, tujuan: '', location_used: '', keterangan: '' });

export default function Inventory({ userRole }) {
  const canEdit   = ['admin', 'superadmin'].includes(userRole);
  const canDelete = userRole === 'superadmin';
  const canUse    = ['admin', 'superadmin', 'technician', 'housekeeping'].includes(userRole);

  const [view, setView] = useState('stok');
  const [items, setItems]           = useState([]);
  const [usages, setUsages]         = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);

  const [search, setSearch]                   = useState('');
  const [filterProperty, setFilterProperty]   = useState('all');
  const [filterCategory, setFilterCategory]   = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');

  // item add/edit
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]         = useState(emptyForm());

  // usage
  const [showUsageModal, setShowUsageModal]   = useState(null); // item object
  const [usageForm, setUsageForm]             = useState(emptyUsage());

  // per-item history
  const [historyItem, setHistoryItem]         = useState(null); // item object
  const [itemHistory, setItemHistory]         = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = async () => {
    try {
      const [invRes, propRes, usageRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inventory`, config()),
        axios.get(`${BACKEND_URL}/api/staff/properties`, config()),
        axios.get(`${BACKEND_URL}/api/inventory/usage`, config()),
      ]);
      setItems(invRes.data);
      setProperties(propRes.data);
      setUsages(usageRes.data);
    } catch {
      setError('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* ── Item CRUD ── */
  const openAdd  = () => { setEditItem(null); setForm(emptyForm()); setError(''); setShowForm(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ property_name: item.property_name, category: item.category, item_name: item.item_name,
      quantity: item.quantity, unit: item.unit, condition_status: item.condition_status,
      location: item.location || '', notes: item.notes || '' });
    setError(''); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      if (editItem) {
        await axios.put(`${BACKEND_URL}/api/inventory/${editItem.item_id}`, form, config());
        setSuccess('Item berhasil diupdate');
      } else {
        await axios.post(`${BACKEND_URL}/api/inventory`, form, config());
        setSuccess('Item berhasil ditambahkan');
      }
      setShowForm(false); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus "${item.item_name}" dari inventory?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/inventory/${item.item_id}`, config());
      setSuccess('Item dihapus'); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menghapus'); }
  };

  /* ── Usage ── */
  const openUsage = (item) => { setShowUsageModal(item); setUsageForm(emptyUsage()); setError(''); };

  const handleUsage = async (e) => {
    e.preventDefault(); setError(''); setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inventory/${showUsageModal.item_id}/usage`, usageForm, config());
      setSuccess('Pengeluaran berhasil dicatat');
      setShowUsageModal(null); fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal mencatat'); }
    finally { setSubmitting(false); }
  };

  /* ── Per-item history ── */
  const openHistory = async (item) => {
    setHistoryItem(item); setItemHistory([]); setHistoryLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory/usage?item_id=${item.item_id}`, config());
      setItemHistory(res.data);
    } catch { setItemHistory([]); }
    finally { setHistoryLoading(false); }
  };

  /* ── Filters ── */
  const filtered = items.filter(it => {
    if (view === 'stok' && !(Number(it.quantity) > 0)) return false;
    if (view === 'habis' && !(Number(it.quantity) <= 0)) return false;
    if (filterProperty !== 'all' && it.property_name !== filterProperty) return false;
    if (filterCategory !== 'all' && it.category !== filterCategory) return false;
    if (filterCondition !== 'all' && it.condition_status !== filterCondition) return false;
    if (search && !it.item_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredUsages = usages.filter(u => {
    if (filterProperty !== 'all' && u.property_name !== filterProperty) return false;
    if (filterCategory !== 'all' && u.category !== filterCategory) return false;
    if (search && !u.item_name.toLowerCase().includes(search.toLowerCase())
      && !u.keterangan.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = filtered.reduce((acc, it) => {
    if (!acc[it.property_name]) acc[it.property_name] = [];
    acc[it.property_name].push(it); return acc;
  }, {});

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {error   && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
            <Package className="w-5 h-5 text-isk-teal" /> Inventory
          </h2>
          <p className="text-xs text-isk-muted mt-0.5">Peralatan, Perlengkapan &amp; Utilitas</p>
        </div>
        {canEdit && view !== 'riwayat' && (
          <Button onClick={openAdd} size="sm" className="bg-isk-teal text-white rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Tambah Item
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Item',      value: items.length,                                          color: 'text-isk-teal'   },
          { label: 'Peralatan',       value: items.filter(i => i.category === 'peralatan').length,  color: 'text-purple-600' },
          { label: 'Perlengkapan',    value: items.filter(i => i.category === 'perlengkapan').length, color: 'text-blue-600' },
          { label: 'Utilitas', value: items.filter(i => i.category === 'utilitas').length, color: 'text-teal-700' },
          { label: 'Perlu Perhatian', value: items.filter(i => i.condition_status !== 'baik').length, color: 'text-red-600'  },
        ].map(s => (
          <div key={s.label} className="glass-card rounded-2xl p-3 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-isk-muted mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* View toggle + Filters */}
      <div className="glass-card rounded-2xl p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'stok',    icon: <Package className="w-3.5 h-3.5" />,     label: 'Stok' },
            { key: 'habis', icon: <MinusCircle className="w-3.5 h-3.5" />, label: 'Stok Habis', badge: items.filter(it => Number(it.quantity) <= 0).length },
            { key: 'riwayat', icon: <History className="w-3.5 h-3.5" />,     label: 'Riwayat Pengeluaran', badge: usages.length },
          ].map(t => (
            <button key={t.key} onClick={() => setView(t.key)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${view === t.key ? 'bg-isk-teal text-white' : 'bg-white/60 text-isk-text hover:bg-white/80'}`}>
              {t.icon}{t.label}
              {t.badge > 0 && <span className={`ml-0.5 text-xs px-1.5 rounded-full ${view === t.key ? 'bg-white/30' : 'bg-isk-teal/10 text-isk-teal'}`}>{t.badge}</span>}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="relative col-span-2 sm:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-isk-muted" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari item..." className="pl-9 rounded-xl text-sm" />
          </div>
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="rounded-xl text-sm"><SelectValue placeholder="Semua Properti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Properti</SelectItem>
              {properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="rounded-xl text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              <SelectItem value="peralatan">Peralatan</SelectItem>
              <SelectItem value="perlengkapan">Perlengkapan</SelectItem>
              <SelectItem value="utilitas">Utilitas</SelectItem>
            </SelectContent>
          </Select>
          {view !== 'riwayat' && (
            <Select value={filterCondition} onValueChange={setFilterCondition}>
              <SelectTrigger className="rounded-xl text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kondisi</SelectItem>
                <SelectItem value="baik">Baik</SelectItem>
                <SelectItem value="rusak_ringan">Rusak Ringan</SelectItem>
                <SelectItem value="rusak_berat">Rusak Berat</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* ── STOK ── */}
      {view !== 'riwayat' && (
        filtered.length === 0 ? (
          <div className="text-center py-12 text-isk-muted">{view === 'habis' ? 'Tidak ada item dengan stok habis' : 'Tidak ada item dengan stok tersedia'}</div>
        ) : Object.entries(grouped).map(([propName, propItems]) => (
          <div key={propName} className="space-y-2">
            <h3 className="text-sm font-semibold text-isk-muted px-1">{propName}</h3>
            <div className="glass-card rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/20 bg-white/30">
                    <th className="px-4 py-4 text-left font-semibold text-isk-text">Nama Item</th>
                    <th className="px-4 py-4 text-left font-semibold text-isk-text whitespace-nowrap">Tanggal Pembelian</th>
                    <th className="px-4 py-4 text-left font-semibold text-isk-text hidden sm:table-cell">Kategori</th>
                    <th className="px-4 py-4 text-center font-semibold text-isk-text">Stok</th>
                    <th className="px-4 py-4 text-left font-semibold text-isk-text hidden md:table-cell">Lokasi</th>
                    <th className="px-4 py-4 text-left font-semibold text-isk-text">Kondisi</th>
                    <th className="px-4 py-4 text-center font-semibold text-isk-text">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {propItems.map((it, idx) => (
                    <tr key={it.item_id} className={`border-b border-gray-200 hover:bg-white/40 ${idx % 2 === 0 ? '' : 'bg-white/20'}`}>
                      <td className="px-4 py-4">
                        <p className="font-medium text-isk-text">{it.item_name}</p>
                        {it.source_request_id && (
                          <p className="text-xs text-isk-muted flex items-center gap-1 mt-0.5">
                            <Link className="w-3 h-3" />{it.source_request_id}
                          </p>
                        )}
                        {it.notes && <p className="text-xs text-isk-muted italic">{it.notes}</p>}
                      </td>
                      <td className="px-4 py-4 text-isk-muted text-xs whitespace-nowrap">{fmtPurchaseDate(it.purchase_date)}</td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_BADGE[it.category] || 'bg-gray-100 text-gray-700'}`}>
                          {CATEGORY_LABEL[it.category] || it.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`font-bold text-base ${Number(it.quantity) <= 0 ? 'text-red-500' : 'text-isk-teal'}`}>{it.quantity}</span>
                        <span className="text-xs text-isk-muted ml-1">{it.unit}</span>
                      </td>
                      <td className="px-4 py-4 text-isk-muted text-xs hidden md:table-cell">{it.location || '-'}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONDITION_BADGE[it.condition_status] || 'bg-gray-100 text-gray-700'}`}>
                          {CONDITION_LABEL[it.condition_status] || it.condition_status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {/* Riwayat per item */}
                          <button onClick={() => openHistory(it)} title="Lihat Riwayat"
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 transition-colors">
                            <ClipboardList className="w-3.5 h-3.5" />
                          </button>
                          {/* Catat pengeluaran */}
                          {canUse && (
                            <button onClick={() => openUsage(it)} title="Catat Pengeluaran"
                              disabled={Number(it.quantity) <= 0}
                              className={`p-1.5 rounded-lg transition-colors ${Number(it.quantity) <= 0 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-orange-50 text-orange-500'}`}>
                              <MinusCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => openEdit(it)} title="Edit"
                              className="p-1.5 rounded-lg hover:bg-isk-teal/10 text-isk-teal transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(it)} title="Hapus"
                              className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* ── RIWAYAT GLOBAL ── */}
      {view === 'riwayat' && (
        filteredUsages.length === 0 ? (
          <div className="text-center py-12 text-isk-muted">Belum ada riwayat pengeluaran</div>
        ) : (
          <div className="glass-card rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/20 bg-white/30">
                  <th className="px-4 py-4 text-left font-semibold text-isk-text">Item</th>
                  <th className="px-4 py-4 text-left font-semibold text-isk-text hidden sm:table-cell">Properti</th>
                  <th className="px-4 py-4 text-center font-semibold text-isk-text">Qty</th>
                  <th className="px-4 py-4 text-left font-semibold text-isk-text">Tujuan &amp; Keterangan</th>
                  <th className="px-4 py-4 text-left font-semibold text-isk-text hidden md:table-cell">Dicatat oleh</th>
                  <th className="px-4 py-4 text-left font-semibold text-isk-text hidden lg:table-cell">Waktu</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsages.map((u, idx) => (
                  <tr key={u.id} className={`border-b border-gray-200 hover:bg-white/40 ${idx % 2 === 0 ? '' : 'bg-white/20'}`}>
                    <td className="px-4 py-4">
                      <p className="font-medium text-isk-text">{u.item_name}</p>
                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${CATEGORY_BADGE[u.category] || 'bg-gray-100 text-gray-700'}`}>
                        {CATEGORY_LABEL[u.category] || u.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-isk-muted text-xs hidden sm:table-cell">{u.property_name}</td>
                    <td className="px-4 py-4 text-center font-bold text-orange-500">
                      -{u.qty_used}<span className="text-xs font-normal text-isk-muted ml-0.5">{u.unit}</span>
                    </td>
                    <td className="px-4 py-4">
                      {u.tujuan && <p className="text-xs font-medium text-isk-teal mb-0.5">{u.tujuan}</p>}
                      <p className="text-isk-text text-xs">{u.keterangan}</p>
                      {u.location_used && <p className="text-xs text-isk-muted mt-0.5">📍 {u.location_used}</p>}
                    </td>
                    <td className="px-4 py-4 text-isk-muted text-xs hidden md:table-cell">{u.recorded_by_name || u.recorded_by}</td>
                    <td className="px-4 py-4 text-isk-muted text-xs hidden lg:table-cell whitespace-nowrap">{fmtDate(u.used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── MODAL: RIWAYAT PER ITEM ── */}
      {historyItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-start p-5 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-isk-text text-base">{historyItem.item_name}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_BADGE[historyItem.category]}`}>
                    {CATEGORY_LABEL[historyItem.category]}
                  </span>
                  <span className="text-xs text-isk-muted">{historyItem.property_name}</span>
                  <span className="text-xs font-semibold text-isk-teal">Stok: {historyItem.quantity} {historyItem.unit}</span>
                </div>
              </div>
              <button onClick={() => setHistoryItem(null)} className="text-isk-muted hover:text-isk-text ml-4 shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              {historyLoading ? (
                <div className="text-center py-8 text-isk-muted">Memuat riwayat...</div>
              ) : itemHistory.length === 0 ? (
                <div className="text-center py-8 text-isk-muted">Belum ada riwayat pengeluaran untuk item ini</div>
              ) : (
                <div className="space-y-3">
                  {itemHistory.map((u) => (
                    <div key={u.id} className="p-3 rounded-xl border border-gray-100 bg-gray-50 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {u.tujuan && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-isk-teal/10 text-isk-teal mb-1">{u.tujuan}</span>
                          )}
                          <p className="text-sm text-isk-text font-medium">{u.keterangan}</p>
                          {u.location_used && (
                            <p className="text-xs text-isk-muted mt-0.5">📍 {u.location_used}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-orange-500">-{u.qty_used} <span className="text-xs font-normal text-isk-muted">{u.unit}</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-isk-muted pt-1 border-t border-gray-200">
                        <span>{u.recorded_by_name || u.recorded_by}</span>
                        <span>·</span>
                        <span>{fmtDate(u.used_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {canUse && (
              <div className="p-4 border-t border-gray-100">
                <Button onClick={() => { setHistoryItem(null); openUsage(historyItem); }}
                  disabled={Number(historyItem.quantity) <= 0}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full">
                  <MinusCircle className="w-4 h-4 mr-2" />
                  Catat Pengeluaran Baru
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: CATAT PENGELUARAN ── */}
      {showUsageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-bold text-isk-text">Catat Pengeluaran</h3>
              <button onClick={() => setShowUsageModal(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-100">
              <p className="text-sm font-semibold text-isk-text">{showUsageModal.item_name}</p>
              <p className="text-xs text-isk-muted mt-0.5">
                {showUsageModal.property_name} · Stok tersedia:&nbsp;
                <span className="font-bold text-isk-teal">{showUsageModal.quantity} {showUsageModal.unit}</span>
              </p>
            </div>
            <form onSubmit={handleUsage} className="space-y-3" noValidate>
              <div>
                <Label>Jumlah Dikeluarkan *</Label>
                <Input type="number" min="1" max={showUsageModal.quantity}
                  value={usageForm.qty_used}
                  onChange={e => setUsageForm({ ...usageForm, qty_used: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Tujuan Penggunaan</Label>
                <Select value={usageForm.tujuan} onValueChange={v => setUsageForm({ ...usageForm, tujuan: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih tujuan (opsional)" /></SelectTrigger>
                  <SelectContent>
                    {TUJUAN_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lokasi Penggunaan</Label>
                <Input
                  value={usageForm.location_used}
                  onChange={e => setUsageForm({ ...usageForm, location_used: e.target.value })}
                  placeholder="Contoh: Kamar 101, Lobby, Dapur Lt.2"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Keterangan Lengkap *</Label>
                <textarea
                  value={usageForm.keterangan}
                  onChange={e => setUsageForm({ ...usageForm, keterangan: e.target.value })}
                  placeholder="Jelaskan secara detail untuk apa item ini digunakan, kondisi saat diambil, dll."
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowUsageModal(null)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-full">
                  {submitting ? 'Menyimpan...' : 'Catat Pengeluaran'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: ADD / EDIT ITEM ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">{editItem ? 'Edit Item' : 'Tambah Item Inventory'}</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3" noValidate>
              {!editItem && (
                <div>
                  <Label>Properti *</Label>
                  <Select value={form.property_name} onValueChange={v => setForm({ ...form, property_name: v })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih properti" /></SelectTrigger>
                    <SelectContent>
                      {properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Kategori *</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peralatan">Peralatan</SelectItem>
                    <SelectItem value="perlengkapan">Perlengkapan</SelectItem>
                    <SelectItem value="utilitas">Utilitas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nama Item *</Label>
                <Input value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} placeholder="Contoh: Sapu Lantai" className="mt-1 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Qty *</Label>
                  <Input type="number" min="0" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Satuan</Label>
                  <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="pcs / set / buah" className="mt-1 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Kondisi</Label>
                <Select value={form.condition_status} onValueChange={v => setForm({ ...form, condition_status: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baik">Baik</SelectItem>
                    <SelectItem value="rusak_ringan">Rusak Ringan</SelectItem>
                    <SelectItem value="rusak_berat">Rusak Berat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lokasi Penyimpanan</Label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Contoh: Gudang Lt.1, Kamar 101" className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Catatan</Label>
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opsional" className="mt-1 rounded-xl" />
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-isk-teal text-white rounded-full">
                  {submitting ? 'Menyimpan...' : editItem ? 'Simpan Perubahan' : 'Tambah Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
